import { useRef, useState, useEffect, type JSX, type MutableRefObject } from 'react'
import Quill from 'quill'
import DOMPurify from 'dompurify'
import { useConfirmDialog } from '../ui/ConfirmDialog.js'

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'])
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

/**
 * Quill's default snow toolbar, minus the code-block button.
 *
 * Code-block used to be abused as a workaround HTML-entry mode (it silently
 * HTML-escaped whatever was typed inside it) before the HTML Source tab
 * existed; keeping it around now just invites the same confusion, so this is
 * the stock header (same buttons, same order, same classNames Quill binds to)
 * with that one button omitted.
 *
 * This is a plain HTML string rather than JSX on purpose: Quill's snow theme
 * *rewrites* the toolbar in place on init (swapping each `<select>` for a
 * `.ql-picker` widget and injecting an `<svg>` icon into every `<button>`).
 * React must not own a subtree another library mutates — so the toolbar is
 * built imperatively into a container React only ever sees as empty, which
 * also makes teardown a single `innerHTML = ''` and keeps StrictMode's
 * double-mount from stacking two toolbars.
 */
const TOOLBAR_HTML = `
  <span class="ql-formats">
    <select class="ql-header">
      <option value="1">Heading</option>
      <option value="2">Subheading</option>
      <option value="0" selected>Normal</option>
    </select>
    <select class="ql-font">
      <option selected></option>
      <option value="serif"></option>
      <option value="monospace"></option>
    </select>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-bold" aria-label="Bold"></button>
    <button type="button" class="ql-italic" aria-label="Italic"></button>
    <button type="button" class="ql-underline" aria-label="Underline"></button>
  </span>
  <span class="ql-formats">
    <select class="ql-color"></select>
    <select class="ql-background"></select>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-list" value="ordered" aria-label="Ordered List"></button>
    <button type="button" class="ql-list" value="bullet" aria-label="Unordered List"></button>
    <select class="ql-align">
      <option selected></option>
      <option value="center"></option>
      <option value="right"></option>
      <option value="justify"></option>
    </select>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-link" aria-label="Insert Link"></button>
    <button type="button" class="ql-image" aria-label="Insert Image"></button>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-clean" aria-label="Remove Styles"></button>
  </span>
`

/** Quill's "document is empty" serialisation, which callers want as `''`. */
const EMPTY_QUILL_HTML = '<p><br></p>'

function readHtml(quill: Quill): string {
  const html = quill.root.innerHTML
  return html === EMPTY_QUILL_HTML ? '' : html
}

export interface LocalImage {
  id: string
  file: File
  objectUrl: string
}

/**
 * Imperative handle for callers that need to splice content in at the caret —
 * variable pickers, snippet buttons. Handed out via {@link RichTextEditorProps.apiRef}
 * so the parent doesn't have to reach into Quill itself.
 */
export interface RichTextEditorApi {
  /** Insert plain text at the caret (end of document if never focused). */
  insertText: (text: string) => void
}

export interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  height?: number
  readOnly?: boolean
  placeholder?: string
  required?: boolean
  className?: string
  label?: string
  error?: string
  id?: string
  onUploadError?: (error: string) => void
  onLocalImagesChange?: (images: LocalImage[]) => void
  apiRef?: MutableRefObject<RichTextEditorApi | null>
}

/**
 * Uploads local images and replaces their blob URLs with uploaded URLs in the HTML content.
 * This function should be called before saving the template.
 * 
 * Uses the tenant-based upload service which automatically:
 * - Uploads to S3 if tenant has S3 enabled
 * - Falls back to local storage if S3 is disabled or fails
 */
export async function uploadLocalImages(
  html: string,
  localImages: LocalImage[],
  upload: (file: File) => Promise<{ success: boolean; storageType: 'S3' | 'LOCAL'; url: string; error?: string }>,
  onProgress?: (current: number, total: number) => void,
): Promise<string> {
  if (localImages.length === 0) {
    return html
  }

  let updatedHtml = html
  
  for (const localImage of localImages) {
    if (!localImage) continue
    onProgress?.(localImages.indexOf(localImage) + 1, localImages.length)
    
    try {
      const result = await upload(localImage.file)
      if (result.success && result.url) {
        updatedHtml = updatedHtml.replace(localImage.objectUrl, result.url)
      } else {
        console.error(`Failed to upload image ${localImage.id}:`, result.error)
      }
    } catch (err) {
      console.error(`Failed to upload image ${localImage.id}:`, err)
      // Continue with other images even if one fails
    }
  }
  
  return updatedHtml
}

export default function RichTextEditor({
  value,
  onChange,
  height = 320,
  readOnly = false,
  placeholder = 'Enter content...',
  required = false,
  className = '',
  label,
  error,
  id = 'rich-text-editor',
  onUploadError,
  onLocalImagesChange,
  apiRef,
}: RichTextEditorProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null)
  const toolbarElRef = useRef<HTMLDivElement | null>(null)
  const containerElRef = useRef<HTMLDivElement | null>(null)
  const quillRef = useRef<Quill | null>(null)
  const localImagesRef = useRef<Map<string, LocalImage>>(new Map())
  const [mode, setMode] = useState<'rich' | 'html'>('rich')
  const { confirm, Dialog: ConfirmDialogRoot } = useConfirmDialog()

  /**
   * Quill is created once and then driven imperatively, so every callback and
   * prop it needs at *event* time is read through a ref. Putting them in the
   * init effect's dependency list instead would tear down and rebuild the
   * editor — losing caret, undo history and focus — every time a caller passes
   * a fresh inline arrow, which all of them do.
   */
  const latest = useRef({ onChange, placeholder, onUploadError, onLocalImagesChange, readOnly })
  latest.current = { onChange, placeholder, onUploadError, onLocalImagesChange, readOnly }

  /**
   * The last `value` this component put into (or took out of) Quill. Compared
   * against the incoming prop to decide whether a render carries a genuinely
   * external change. It stores the *prop* string rather than Quill's
   * serialisation of it: Quill normalises HTML on the way in, so comparing
   * against the serialised form would make every external update look like it
   * "didn't take" and re-apply it on each render, wiping the caret each time.
   */
  const appliedValueRef = useRef<string>(value)

  const handleSwitchToRichText = (): void => {
    if (isQuillRiskyHtml(value)) {
      void confirm({
        title: 'Switch to Rich Text view?',
        message: 'Switching to Rich Text view may alter complex HTML formatting (tables, custom styles). Continue?',
        confirmLabel: 'Switch Anyway',
        cancelLabel: 'Cancel',
        onConfirm: () => setMode('rich'),
      })
      return
    }
    setMode('rich')
  }

  const validateImageFile = (file: File): { valid: boolean; error?: string } => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      return { valid: false, error: `Unsupported file type: ${file.type}. Allowed: JPEG, PNG, GIF, WEBP, SVG` }
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      return { valid: false, error: `File too large. Maximum size: ${MAX_IMAGE_SIZE_BYTES / 1024 / 1024}MB` }
    }
    return { valid: true }
  }

  const createLocalImage = async (file: File): Promise<LocalImage | null> => {
    const validation = validateImageFile(file)
    if (!validation.valid) {
      latest.current.onUploadError?.(validation.error ?? 'Invalid file')
      return null
    }

    const imageId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Read file as Data URL to bypass Quill's strict image sanitization (which strips blob: URIs)
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
    
    const localImage: LocalImage = {
      id: imageId,
      file,
      objectUrl: dataUrl,
    }
    
    localImagesRef.current.set(imageId, localImage)
    notifyLocalImagesChange()
    
    return localImage
  }

  const removeLocalImage = (imageId: string): void => {
    const localImage = localImagesRef.current.get(imageId)
    if (localImage) {
      localImagesRef.current.delete(imageId)
      notifyLocalImagesChange()
    }
  }

  const notifyLocalImagesChange = (): void => {
    latest.current.onLocalImagesChange?.(Array.from(localImagesRef.current.values()))
  }

  const updateLocalImagesFromContent = (html: string): void => {
    const imgRegex = /<img[^>]+src="([^"]+)"/g
    const matches = Array.from(html.matchAll(imgRegex))
    const currentImageIds = new Set<string>()
    
    matches.forEach((match) => {
      const src = match[1]
      if (src && src.startsWith('data:image/')) {
        for (const [imageId, img] of localImagesRef.current.entries()) {
          if (img.objectUrl === src) {
            currentImageIds.add(imageId)
            break
          }
        }
      }
    })
    
    // Remove images that are no longer in the content
    for (const [imageId] of localImagesRef.current.entries()) {
      if (!currentImageIds.has(imageId)) {
        removeLocalImage(imageId)
      }
    }
  }

  const insertImage = (localImage: LocalImage): void => {
    const quill = quillRef.current
    if (!quill) return

    const range = quill.getSelection(true)
    const index = range ? range.index : quill.getLength()

    // Insert image as HTML with inline styles for better control
    const imgHtml = `<img src="${localImage.objectUrl}" style="max-width: 100%; height: auto; display: block;" />`
    quill.clipboard.dangerouslyPasteHTML(index, imgHtml)
    quill.setSelection(index + 1)
  }

  const handleDrop = async (e: DragEvent): Promise<void> => {
    if (latest.current.readOnly) return

    const files = Array.from(e.dataTransfer?.files ?? [])
    const imageFile = files.find((file) => ALLOWED_IMAGE_TYPES.has(file.type))

    // Only swallow the drop when we're the ones handling it — otherwise let
    // Quill's own handler run so dropped text/HTML still lands in the document.
    if (imageFile) {
      e.preventDefault()
      const localImage = await createLocalImage(imageFile)
      if (localImage) {
        insertImage(localImage)
      }
    }
  }

  const handlePaste = async (e: ClipboardEvent): Promise<void> => {
    if (latest.current.readOnly) return

    const items = Array.from(e.clipboardData?.items ?? [])
    const imageItem = items.find((item) => item.type.startsWith('image/'))

    if (imageItem) {
      e.preventDefault()
      const file = imageItem.getAsFile()
      if (file) {
        const localImage = await createLocalImage(file)
        if (localImage) {
          insertImage(localImage)
        }
      }
    }
  }

  const pickAndInsertImage = (): void => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.onchange = async () => {
      const file = input.files?.[0]
      if (file) {
        const localImage = await createLocalImage(file)
        if (localImage) {
          insertImage(localImage)
        }
      }
    }
    input.click()
  }

  /**
   * The mount effect closes over the first render's copies of the handlers
   * above, so it reaches them through this ref instead — kept current on every
   * render, same reason as `latest`.
   */
  const handlers = useRef({ handleDrop, handlePaste, pickAndInsertImage })
  handlers.current = { handleDrop, handlePaste, pickAndInsertImage }

  /**
   * Build the editor. Runs once per mount of the Rich Text tab — Quill has no
   * `destroy()`, so teardown is "drop the reference and empty the container it
   * lives in", which is enough because every node it created is inside `host`.
   */
  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const toolbarEl = document.createElement('div')
    toolbarEl.innerHTML = TOOLBAR_HTML
    const containerEl = document.createElement('div')
    host.append(toolbarEl, containerEl)

    const quill = new Quill(containerEl, {
      theme: 'snow',
      placeholder: latest.current.placeholder,
      modules: { toolbar: toolbarEl },
    })
    quillRef.current = quill
    toolbarElRef.current = toolbarEl
    containerElRef.current = containerEl

    // Seed the initial value silently: this is us writing what the caller
    // already has, not an edit worth echoing back through `onChange`.
    if (appliedValueRef.current) {
      quill.setContents(quill.clipboard.convert({ html: appliedValueRef.current }), 'silent')
    }

    quill.on('text-change', (_delta, _old, source) => {
      if (source === 'silent') return
      const html = readHtml(quill)
      appliedValueRef.current = html
      latest.current.onChange(html)
      updateLocalImagesFromContent(html)
    })

    // Local-preview image insert instead of Quill's base64-prompt default.
    const toolbar = quill.getModule('toolbar') as { addHandler: (f: string, h: () => void) => void }
    toolbar.addHandler('image', () => handlers.current.pickAndInsertImage())

    const onDrop = (e: Event): void => { void handlers.current.handleDrop(e as DragEvent) }
    const onPaste = (e: Event): void => { void handlers.current.handlePaste(e as ClipboardEvent) }
    quill.root.addEventListener('drop', onDrop)
    quill.root.addEventListener('paste', onPaste)

    return () => {
      quill.root.removeEventListener('drop', onDrop)
      quill.root.removeEventListener('paste', onPaste)
      quillRef.current = null
      toolbarElRef.current = null
      containerElRef.current = null
      host.innerHTML = ''
    }
  }, [mode])

  /** Push externally-changed values in (async form loads, AI compose, resets). */
  useEffect(() => {
    const quill = quillRef.current
    if (!quill || value === appliedValueRef.current) return
    appliedValueRef.current = value
    const selection = quill.getSelection()
    quill.setContents(quill.clipboard.convert({ html: value }), 'silent')
    if (selection) {
      quill.setSelection(Math.min(selection.index, quill.getLength() - 1), 0, 'silent')
    }
  }, [value])

  /* readOnly toggles mid-edit (view mode, in-flight saves) — flip Quill in
     place rather than rebuilding it, so focus and history survive a save. */
  useEffect(() => {
    quillRef.current?.enable(!readOnly)
    if (toolbarElRef.current) {
      toolbarElRef.current.style.display = readOnly ? 'none' : ''
    }
  }, [readOnly, mode])

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.root.dataset['placeholder'] = placeholder
    }
  }, [placeholder, mode])

  useEffect(() => {
    if (containerElRef.current) {
      containerElRef.current.style.height = `${height}px`
    }
  }, [height, mode])

  // Expose the caret-insert helper. Quill's own change event fires from the
  // insert, so `onChange` still carries the new HTML — no extra plumbing.
  useEffect(() => {
    if (!apiRef) return
    apiRef.current = {
      insertText: (text: string) => {
        const quill = quillRef.current
        if (!quill) return
        const range = quill.getSelection()
        const index = range ? range.index + range.length : quill.getLength() - 1
        quill.insertText(Math.max(index, 0), text, 'user')
        quill.setSelection(Math.max(index, 0) + text.length, 0)
        quill.focus()
      },
    }
    return () => {
      apiRef.current = null
    }
  }, [apiRef])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localImagesRef.current.clear()
    }
  }, [])

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
          {required && (
            <span className="ml-1 text-red-500">*</span>
          )}
        </label>
      )}

      <div className="flex gap-1 border-b border-gray-200">
        <button
          type="button"
          onClick={handleSwitchToRichText}
          disabled={readOnly}
          className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors ${
            mode === 'rich' ? 'bg-white text-indigo-600 border border-b-0 border-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Rich Text
        </button>
        <button
          type="button"
          onClick={() => setMode('html')}
          disabled={readOnly}
          className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors ${
            mode === 'html' ? 'bg-white text-indigo-600 border border-b-0 border-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          HTML Source
        </button>
      </div>

      {mode === 'rich' ? (
        /* Quill builds its toolbar and document inside this node. React must
           render it empty and never give it children of its own. */
        <div
          ref={hostRef}
          id={id}
          className={`kc3-editor${error ? ' kc3-editor--error' : ''}`}
        />
      ) : (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={placeholder}
          style={{ height }}
          className={`w-full font-mono text-xs p-3 border rounded-b-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
            error ? 'border-red-500' : 'border-gray-200'
          }`}
        />
      )}

      {error && (
        <p
          role="alert"
          className="text-xs text-red-600"
        >
          {error}
        </p>
      )}

      <ConfirmDialogRoot />
    </div>
  )
}

/**
 * Checks if HTML content is effectively empty.
 */
export function isEmptyHtml(
  html: string | null | undefined,
): boolean {
  if (!html) return true

  // Check if content contains an image - if so, it's not empty
  if (/<img[^>]+src="[^"]+"/.test(html)) {
    return false
  }

  const cleaned = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim()

  return cleaned.length === 0
}

export interface RichTextViewerProps {
  content: string
  className?: string
  raw?: boolean // If true, render HTML exactly as-is without sanitization
}

/**
 * Resolves a single relative URL to an absolute URL using the current origin.
 * Handles both plain URL strings (e.g., /uploads/...) and full URLs (returns as-is).
 */
export function resolveRelativeUrl(url: string): string {
  if (!url) return url
  if (url.startsWith('/') && !url.startsWith('//')) {
    return `${window.location.origin}${url}`
  }
  return url
}

/**
 * Converts relative image URLs to absolute URLs using the current origin.
 * This ensures images stored with relative paths (e.g., /uploads/...) render correctly.
 * Also handles plain URL strings that aren't in HTML tags.
 */
export function convertRelativeUrlsToAbsolute(html: string): string {
  if (!html) return html
  // If the value is a plain relative URL (not HTML), resolve it directly
  if (html.startsWith('/') && !html.includes('<')) {
    return resolveRelativeUrl(html)
  }
  const baseUrl = window.location.origin
  return html.replace(/<img([^>]+)src="([^"]+)"([^>]*)>/g, (match, before, src, after) => {
    // If src is a relative path (starts with /), prepend the origin
    if (src.startsWith('/') && !src.startsWith('//')) {
      return `<img${before}src="${baseUrl}${src}"${after}>`
    }
    return match
  })
}


/**
 * Decode HTML entities to convert escaped HTML back to actual HTML
 * Example: &lt;h1&gt; becomes <h1>
 */
function decodeHtml(html: string): string {
  if (typeof document === 'undefined') return html
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

/**
 * Safe HTML renderer.
 */
export function RichTextViewer({
  content,
  className = '',
  raw = false,
}: RichTextViewerProps): JSX.Element {
  // Decode HTML entities to render actual HTML
  const decodedContent = decodeHtml(content)
  const processedContent = convertRelativeUrlsToAbsolute(decodedContent)

  // Raw mode shows an email template the way it will be laid out, so layout markup (style blocks, tables,
  // inline styles, images) is kept. It is still sanitized (SE-20): scripts, event handlers, javascript: and
  // data: links, objects, forms and base/meta tags are removed, so a template containing them cannot run code.
  if (raw) {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(processedContent, {
            ADD_TAGS: ['style'],
            ADD_ATTR: ['target'],
            FORCE_BODY: true, // keeps a leading <style> block instead of treating it as <head>
            FORBID_TAGS: ['script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'form', 'input', 'button', 'select', 'textarea', 'link', 'meta', 'base'],
            FORBID_ATTR: ['srcdoc', 'formaction', 'action'],
          }),
        }}
      />
    )
  }

  // In normal mode, sanitize for security
  return (
    <div
      className={`rich-text-content prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(processedContent, {
          // SE-20: no data: or blob: for links and frames (a data:text/html link or frame can carry script); pasted inline
          // images still work because DOMPurify allows data: on <img> by itself.
          ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
          ADD_TAGS: ['iframe', 'style', 'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'span', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'hr', 'table', 'tbody', 'tr', 'td', 'th', 'a', 'img'],
          ADD_ATTR: ['target', 'style', 'class', 'id', 'width', 'height', 'frameborder', 'allowfullscreen', 'data-language', 'href', 'src', 'alt'],
          ALLOW_DATA_ATTR: true,
          KEEP_CONTENT: true,
        }),
      }}
    />
  )
}

const QUILL_RISKY_TAGS = /<(table|thead|tbody|tfoot|tr|td|th|style|head|html|iframe|script)\b/i
const INLINE_STYLE_ATTR = /style="([^"]*)"/gi

/**
 * True when re-parsing this HTML through Quill's editor is likely to lose
 * or alter structure (tables, <style> blocks, full documents, or elements
 * with many inline style properties). Used to warn before switching from
 * HTML Source back to Rich Text view.
 */
export function isQuillRiskyHtml(html: string): boolean {
  if (!html) return false
  if (QUILL_RISKY_TAGS.test(html)) return true
  if (/<!DOCTYPE/i.test(html)) return true

  for (const match of html.matchAll(INLINE_STYLE_ATTR)) {
    const styleBody = match[1] ?? ''
    const propertyCount = styleBody.split(';').filter((s) => s.trim().length > 0).length
    if (propertyCount > 3) return true
  }

  return false
}
